import type { ChangeEvent, ReactNode } from "react";
import "./ChoiceGroup.css";

export interface ChoiceOption {
  value: string;
  label: ReactNode;
  /** grey line under the label (list layout) */
  description?: ReactNode;
  /** on the right of the box, e.g. a price or an arrow (list layout) */
  aside?: ReactNode;
  disabled?: boolean;
}

export interface ChoiceGroupProps {
  /** radio group name, sent in the change event */
  name: string;
  legend?: ReactNode;
  options: ChoiceOption[];
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: ReactNode;
  /** "grid" (default): small boxes side by side; "list": one wide box per
      row with description and aside, e.g. price plans */
  layout?: "grid" | "list";
  className?: string;
}

/**
 * Pick one of a few options, shown as boxes with a radio dot.
 * Example: <ChoiceGroup name="document_type" legend="Document type"
 *   options={[{ value: "dni_nie", label: "DNI / NIE" }]} value={v} onChange={fn} />
 */
const ChoiceGroup = ({
  name,
  legend,
  options,
  value,
  onChange,
  disabled = false,
  error,
  layout = "grid",
  className = "",
}: ChoiceGroupProps) => (
  <fieldset
    className={`ds-field ds-choice ds-choice--${layout} ${
      error ? "has-error" : ""
    } ${className}`.trim()}
    disabled={disabled}
  >
    {legend && <legend className="ds-field__label">{legend}</legend>}
    <div className="ds-choice__options">
      {options.map((option) => (
        <label
          key={option.value}
          className={`ds-choice__option ${
            value === option.value ? "is-selected" : ""
          }`}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            disabled={option.disabled}
            onChange={onChange}
          />
          <span className="ds-choice__text">
            <span className="ds-choice__label">{option.label}</span>
            {option.description && (
              <span className="ds-choice__description">
                {option.description}
              </span>
            )}
          </span>
          {option.aside && (
            <span className="ds-choice__aside">{option.aside}</span>
          )}
        </label>
      ))}
    </div>
    {error && typeof error !== "boolean" && (
      <p className="ds-field__error" role="alert">
        {error}
      </p>
    )}
  </fieldset>
);

export default ChoiceGroup;
